import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { api, getUser } from "../api";
import { PeriodFilter, matchPeriod } from "./PeriodFilter";

const T = {
  it: { received: "Ricevuti", emitted: "Emessi", none_r: "Nessun documento fiscale ricevuto.", none_e: "Nessun documento depositato.", from: "Emittente", to: "Destinatario", open: "Apri PDF", pending: "In attesa / riservato", delivered: "Consegnato", amount: "Importo", date: "Data di emissione", types: { invoice: "Fattura", receipt: "Ricevuta", credit_note: "Nota di credito", other: "Documento" } },
  en: { received: "Received", emitted: "Issued", none_r: "No fiscal documents received.", none_e: "No documents deposited.", from: "Issuer", to: "Recipient", open: "Open PDF", pending: "Pending / reserved", delivered: "Delivered", amount: "Amount", date: "Issue date", types: { invoice: "Invoice", receipt: "Receipt", credit_note: "Credit note", other: "Document" } },
  de: { received: "Erhalten", emitted: "Ausgestellt", none_r: "Keine Steuerdokumente erhalten.", none_e: "Keine Dokumente hinterlegt.", from: "Aussteller", to: "Empfänger", open: "PDF öffnen", pending: "Ausstehend / reserviert", delivered: "Zugestellt", amount: "Betrag", date: "Ausstellungsdatum", types: { invoice: "Rechnung", receipt: "Beleg", credit_note: "Gutschrift", other: "Dokument" } },
  fr: { received: "Reçus", emitted: "Émis", none_r: "Aucun document fiscal reçu.", none_e: "Aucun document déposé.", from: "Émetteur", to: "Destinataire", open: "Ouvrir le PDF", pending: "En attente / réservé", delivered: "Livré", amount: "Montant", date: "Date d'émission", types: { invoice: "Facture", receipt: "Reçu", credit_note: "Note de crédit", other: "Document" } },
};

export const FiscalDocs = () => {
  const { i18n } = useTranslation();
  const L = T[(i18n.language || "it").slice(0, 2)] || T.it;
  const me = getUser();
  const isEmitter = me && (me.role === "professional" || me.role === "organization");
  const [tab, setTab] = useState(isEmitter ? "emitted" : "received");
  const [received, setReceived] = useState([]);
  const [emitted, setEmitted] = useState([]);
  const [fMon, setFMon] = useState(""); const [fYr, setFYr] = useState("");

  useEffect(() => {
    api.get("/pam-bill/received").then(({ data }) => setReceived(data.documents)).catch(() => {});
    if (isEmitter) api.get("/pam-bill/documents").then(({ data }) => setEmitted(data.documents)).catch(() => {});
  }, [isEmitter]);

  const open = (id) => api.get(`/pam-bill/documents/${id}/file`, { responseType: "blob" }).then((r) => window.open(URL.createObjectURL(r.data)));

  const title = (d) => {
    const base = (L.types[d.doc_type] || L.types.other) + (d.ips ? " IPS" : "");
    return d.number ? `${base} n. ${d.number}` : base;
  };
  const emitterLabel = (d) => (d.ips ? "IPS srl Società Benefit" : d.sender || "—");

  const base = tab === "emitted" ? emitted : received;
  const periodYears = Array.from(new Set(base.map((d) => (d.issue_date || d.delivered_at || d.created_at || "").slice(0, 4)).filter(Boolean))).sort().reverse();
  const list = base.filter((d) => matchPeriod(d.issue_date || d.delivered_at || d.created_at, fMon, fYr));
  const empty = tab === "emitted" ? L.none_e : L.none_r;

  return (<div data-testid="fiscal-docs">
    {isEmitter && (
      <div className="flex gap-2 mb-4">
        {[["emitted", L.emitted], ["received", L.received]].map(([k, l]) => (
          <button key={k} className={`yp-btn ${tab === k ? "yp-btn-primary" : "yp-btn-ghost"} !py-1 !px-3 text-sm`} onClick={() => setTab(k)} data-testid={`fiscal-tab-${k}`}>{l}</button>))}
      </div>
    )}
    <div className="flex justify-end mb-3"><PeriodFilter mon={fMon} yr={fYr} setMon={setFMon} setYr={setFYr} years={periodYears} testid="fiscal-filter" /></div>
    {list.length === 0 && <div className="text-slate-400 text-sm" data-testid="fiscal-empty">{empty}</div>}
    <div className="space-y-2">
      {list.map((d) => (
        <div key={d.id} className="yp-card p-3 flex items-center justify-between gap-3" data-testid="fiscal-row">
          <div className="text-sm min-w-0">
            <div className="font-medium" data-testid="fiscal-title">{title(d)}</div>
            <div className="text-xs text-slate-500 mt-0.5 space-y-0.5">
              {d.amount_cents != null && <div data-testid="fiscal-amount"><span className="text-slate-400">{L.amount}:</span> €{((d.amount_cents || 0) / 100).toFixed(2)}</div>}
              <div data-testid="fiscal-party"><span className="text-slate-400">{tab === "emitted" ? L.to : L.from}:</span> {tab === "emitted" ? (d.recipient_account_id ? `✓ ${L.delivered}` : L.pending) : emitterLabel(d)}</div>
              {d.description && <div className="text-slate-400" data-testid="fiscal-desc">{d.description}</div>}
              {(d.issue_date || d.delivered_at || d.created_at) && <div data-testid="fiscal-date"><span className="text-slate-400">{L.date}:</span> {(d.issue_date || d.delivered_at || d.created_at || "").slice(0, 10)}</div>}
            </div>
          </div>
          <button className="yp-btn yp-btn-outline !py-1 !px-3 text-sm shrink-0" onClick={() => open(d.id)} data-testid="fiscal-open">{L.open}</button>
        </div>
      ))}
    </div>
  </div>);
};
